import { FC, memo, useMemo } from 'react'
import classNames from 'classnames'

import { HTMLEDITOR_VIEW_STYLE } from 'configs/constants'

import { useAppSelector } from 'states/hooks'
import {
  addCodeStyleByCodeTarget,
  addMentionStyleByMentionTarget,
} from 'utils/chat'
import { CrewHtmlContent } from 'components/elements/crewHtmlContent/crewHtmlContent'
import { sanitizeHtml, sanitizeHtmlAllowOnlyEmoji } from 'utils/html'
import { escapeRegExp } from '@crew/utils/chat'

export type CrewNormalChatMessageProps = {
  messageText: string
  messagePlainText: string
  isTopicSummary: boolean
  highlightKeyword?: string
}

export const CrewNormalChatMessage: FC<CrewNormalChatMessageProps> = memo(
  (props) => {
    const loggedInUser = useAppSelector((state) => state.app.loggedInUser)
    // Replace to mark keyword with background color when searching
    const htmlMessageResult = useMemo(() => {
      // 再掲トピックの場合はハイライト不要なのでサニタイズだけして返す
      if (props.isTopicSummary) {
        // 再掲トピックの場合は、pタグやspanタグの出力やclassによる装飾を防ぐため、
        // sanitizeHtmlAllowOnlyEmojiにより「入力された文字列のみ」を表示するようにしている
        return sanitizeHtmlAllowOnlyEmoji(props.messageText)
      }
      let messageResult = props.messageText
      if (props.highlightKeyword && props.highlightKeyword.trim().length > 0) {
        // Escape the highlight keyword
        const escapedKeyword = escapeRegExp(props.highlightKeyword)

        // Regex replace text outside html tags
        const keywords = escapedKeyword.split(' ')
        const regex = new RegExp(`(${keywords.join('|')})(?![^<]*>)`, 'gi')

        // $1: regex matched string
        messageResult = props.messageText.replace(
          regex,
          '<strong class="bg-crew-yellow-2-light dark:bg-crew-yellow-2-dark">$1</strong>'
        )
      }

      messageResult = addCodeStyleByCodeTarget(messageResult) // チャット本文

      return addMentionStyleByMentionTarget(
        sanitizeHtml(messageResult),
        loggedInUser?.id ?? ''
      ) // チャット本文
    }, [
      props.isTopicSummary,
      props.messageText,
      props.highlightKeyword,
      loggedInUser?.id,
    ])

    return (
      <div
        className={classNames(
          HTMLEDITOR_VIEW_STYLE,
          props.isTopicSummary && 'truncate' // スレッドの見出しの場合は1行表示とし、はみ出た分は省略表示する
        )}
      >
        <CrewHtmlContent html={htmlMessageResult} />
      </div>
    )
  }
)
